import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const LEAD_SOURCES = ['walk-in', 'referral', 'field', 'phone', 'social-media', 'campaign', 'other'];

export default function CreateLead() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    nationalId: '',
    address: '',
    source: 'walk-in',
    notes: '',
    followUpDate: '',
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);

  // ADDED: Fetch branches and users for dropdowns (if user is admin/manager)
  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [branchesRes, usersRes] = await Promise.all([
          api.get('/branches?limit=100').catch(() => null),
          api.get('/users?limit=100').catch(() => null),
        ]);
        if (branchesRes?.data?.data) {
          const b = branchesRes.data.data.branches || branchesRes.data.data || [];
          setBranches(Array.isArray(b) ? b : []);
        }
        if (usersRes?.data?.data) {
          const u = usersRes.data.data.users || usersRes.data.data || [];
          setUsers(Array.isArray(u) ? u : []);
        }
      } catch {
        // Non-critical — dropdowns just won't populate
      }
    };
    fetchMeta();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Clean up empty optional fields
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (!payload.nationalId) delete payload.nationalId;
      if (!payload.address) delete payload.address;
      if (!payload.notes) delete payload.notes;
      if (!payload.followUpDate) delete payload.followUpDate;
      if (!payload.branch) delete payload.branch;
      if (!payload.assignedTo) delete payload.assignedTo;

      await api.post('/leads', payload);
      navigate('/leads');
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create lead';
      // Handle duplicate phone error
      if (err.response?.status === 409) {
        setError(msg);
      } else if (err.response?.data?.errors) {
        setError(err.response.data.errors.map((e) => e.message).join(', '));
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ maxWidth: '700px', margin: '0 auto' }}>
      <h3 className="mb-2">Create New Lead</h3>

      {error && (
        <div style={{ color: 'red', marginBottom: 16, padding: '8px 12px', background: '#fef2f2', borderRadius: 6, fontSize: 14 }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Name Row */}
        <div className="grid grid-2 gap-1">
          <div className="form-group">
            <label>First Name *</label>
            <input
              className="form-control"
              name="firstName"
              required
              maxLength={50}
              value={form.firstName}
              onChange={handleChange}
              placeholder="Enter first name"
            />
          </div>
          <div className="form-group">
            <label>Last Name *</label>
            <input
              className="form-control"
              name="lastName"
              required
              maxLength={50}
              value={form.lastName}
              onChange={handleChange}
              placeholder="Enter last name"
            />
          </div>
        </div>

        {/* Phone & Email Row */}
        <div className="grid grid-2 gap-1">
          <div className="form-group">
            <label>Phone *</label>
            <input
              className="form-control"
              name="phone"
              required
              value={form.phone}
              onChange={handleChange}
              placeholder="e.g. +255700000000"
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              className="form-control"
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              placeholder="optional@email.com"
            />
          </div>
        </div>

        {/* National ID & Source Row */}
        <div className="grid grid-2 gap-1">
          <div className="form-group">
            <label>National ID</label>
            <input
              className="form-control"
              name="nationalId"
              value={form.nationalId}
              onChange={handleChange}
              placeholder="Optional"
            />
          </div>
          <div className="form-group">
            <label>Source</label>
            <select className="form-control" name="source" value={form.source} onChange={handleChange}>
              {LEAD_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1).replace('-', ' ')}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Branch & Assigned To Row (ADDED) */}
        {(branches.length > 0 || users.length > 0) && (
          <div className="grid grid-2 gap-1">
            {branches.length > 0 && (
              <div className="form-group">
                <label>Branch</label>
                <select className="form-control" name="branch" value={form.branch || ''} onChange={handleChange}>
                  <option value="">Select branch (optional)</option>
                  {branches.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.name} ({b.branchCode})
                    </option>
                  ))}
                </select>
              </div>
            )}
            {users.length > 0 && (
              <div className="form-group">
                <label>Assign To</label>
                <select className="form-control" name="assignedTo" value={form.assignedTo || ''} onChange={handleChange}>
                  <option value="">Assign to yourself</option>
                  {users.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.firstName} {u.lastName}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Address (ADDED) */}
        <div className="form-group">
          <label>Address</label>
          <input
            className="form-control"
            name="address"
            value={form.address}
            onChange={handleChange}
            placeholder="Street, city, area..."
          />
        </div>

        {/* Follow-up Date (ADDED) */}
        <div className="form-group">
          <label>Follow-up Date</label>
          <input
            className="form-control"
            type="date"
            name="followUpDate"
            value={form.followUpDate}
            onChange={handleChange}
          />
        </div>

        {/* Notes */}
        <div className="form-group">
          <label>Notes</label>
          <textarea
            className="form-control"
            rows="3"
            name="notes"
            maxLength={2000}
            value={form.notes}
            onChange={handleChange}
            placeholder="Additional information about this lead..."
          />
        </div>

        {/* Actions */}
        <div className="flex gap-1 mt-2">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Lead'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/leads')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}